import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  emptyNarrationTrack,
  pronunciationEntrySchema,
  slideTtsSettingsSchema,
  textBodyToPlainText,
  walkShapes,
  type NarrationTake,
  type NarrationTrack,
  type CourseData,
  type PronunciationEntry,
  type SlideTtsSettings,
} from '@slideforge/shared';
import {
  detectPronunciationProblems,
  pronunciationDictionaryConsentContent,
  sha256Hex,
  SlideForgeTtsClient,
  syncMarkersFromCharacterOffsets,
  synthesisCacheKey,
  type SynthesisRequest,
  type SynthesisResult,
  type Voice,
} from '@slideforge/tts';
import { putClip } from '../audio/blob-store.js';
import { useStrings } from '../i18n/context.js';
import type { SlideForgeState } from '../state/useSlideForge.js';

const DEFAULT_SLIDE_TTS = slideTtsSettingsSchema.parse({});

export function TtsPanel({
  state,
  track,
  onSave,
}: {
  readonly state: SlideForgeState;
  readonly track: NarrationTrack | undefined;
  readonly onSave: (next: NarrationTrack) => Promise<void>;
}) {
  const t = useStrings();
  const client = useMemo(() => new SlideForgeTtsClient(), []);
  const [apiKey, setApiKey] = useState('');
  const [voices, setVoices] = useState<readonly Voice[]>([]);
  const [styleFilter, setStyleFilter] = useState('');
  const [busy, setBusy] = useState<'credential' | 'voices' | 'synthesis' | 'lexicon' | 'batch' | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showLexiconConsent, setShowLexiconConsent] = useState(false);
  const [lexiconConsentChecked, setLexiconConsentChecked] = useState(false);
  const [lexiconWritten, setLexiconWritten] = useState('');
  const [lexiconPronunciation, setLexiconPronunciation] = useState('');
  const [batchJobs, setBatchJobs] = useState<readonly BatchJob[]>([]);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | undefined>(undefined);
  const [batchRunning, setBatchRunning] = useState(false);
  const [showBatchConsent, setShowBatchConsent] = useState(false);
  const [batchConsentChecked, setBatchConsentChecked] = useState(false);
  const batchAbortRef = useRef<AbortController | undefined>(undefined);
  const [sourceHash, setSourceHash] = useState<string | undefined>(undefined);
  const [currentSynthesisKey, setCurrentSynthesisKey] = useState<string | undefined>(undefined);

  const tts = track?.tts ?? DEFAULT_SLIDE_TTS;
  const parsedSlide = state.deck.parsed?.deck.slides.find((slide) => slide.id === state.selectedSlideId);
  const notesText = parsedSlide?.notes ? textBodyToPlainText(parsedSlide.notes) : '';
  const bodyText = parsedSlide ? slideBodyText(parsedSlide.shapes) : '';
  const effectiveText = tts.textSource === 'script' ? tts.script : tts.textSource === 'body' ? bodyText : notesText;
  const deckCharacters = state.deck.parsed?.deck.slides.reduce(
    (total, slide) => total + (slide.notes ? textBodyToPlainText(slide.notes).length : 0),
    0,
  ) ?? 0;
  const problems = useMemo(() => detectPronunciationProblems(effectiveText), [effectiveText]);
  const effectiveVoiceId = tts.voiceId || state.course.tts.voiceId;
  const pronunciationDictionaryLocators = useMemo(
    () => state.course.tts.pronunciationDictionary
      ? [state.course.tts.pronunciationDictionary]
      : undefined,
    [state.course.tts.pronunciationDictionary],
  );

  useEffect(() => {
    let current = true;
    const request = effectiveVoiceId ? synthesisRequestFor(
      effectiveText,
      effectiveVoiceId,
      tts,
      state.course,
      pronunciationDictionaryLocators,
    ) : undefined;
    void Promise.all([
      sha256Hex(effectiveText),
      request ? synthesisCacheKey(request) : Promise.resolve(undefined),
    ]).then(([hash, key]) => {
      if (current) {
        setSourceHash(hash);
        setCurrentSynthesisKey(key);
      }
    });
    return () => {
      current = false;
    };
  }, [effectiveText, effectiveVoiceId, pronunciationDictionaryLocators, state.course, tts]);

  useEffect(() => () => batchAbortRef.current?.abort(), []);

  const stale = Boolean(
    track?.tts?.sourceHash && sourceHash && (
      track.tts.sourceHash !== sourceHash ||
      track.tts.synthesisKey !== currentSynthesisKey ||
      track.takes.find((take) => take.id === track.activeTakeId)?.synthesisKey !== currentSynthesisKey
    ),
  );
  const styles = [...new Set(voices.flatMap((voice) => voice.styles))].sort((a, b) => a.localeCompare(b));
  const filteredVoices = styleFilter
    ? voices.filter((voice) => voice.styles.includes(styleFilter))
    : voices;
  const effectiveVoice = voices.find((voice) => voice.id === effectiveVoiceId);
  const lexiconNeedsSync = state.course.tts.lexicon.length > 0 && !state.course.tts.pronunciationDictionary;
  const lexiconSyncRequest = useMemo(() => ({
    courseId: state.course.courseId,
    ...(state.course.tts.pronunciationDictionary
      ? { dictionaryId: state.course.tts.pronunciationDictionary.id }
      : {}),
    rules: state.course.tts.lexicon.map((entry) => ({
      written: entry.written,
      pronunciation: entry.pronunciation,
      alphabet: entry.alphabet,
    })),
  }), [state.course.courseId, state.course.tts.lexicon, state.course.tts.pronunciationDictionary]);

  const saveTts = useCallback(
    async (next: SlideTtsSettings) => {
      const base = track ?? emptyNarrationTrack();
      await onSave({ ...base, tts: slideTtsSettingsSchema.parse(next) });
    },
    [track, onSave],
  );

  const loadVoices = useCallback(async () => {
    setBusy('voices');
    setError(undefined);
    try {
      setVoices(await client.listVoices(state.course.language));
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(undefined);
    }
  }, [client, state.course.language]);

  const storeCredential = useCallback(async () => {
    if (!apiKey) return;
    setBusy('credential');
    setError(undefined);
    try {
      await client.saveElevenLabsCredential(apiKey);
      setApiKey('');
      await loadVoices();
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(undefined);
    }
  }, [apiKey, client, loadVoices]);

  const synthesize = useCallback(async () => {
    if (!sourceHash || !effectiveText || !effectiveVoiceId) return;
    setBusy('synthesis');
    setError(undefined);
    try {
      const request = synthesisRequestFor(
        effectiveText,
        effectiveVoiceId,
        tts,
        state.course,
        pronunciationDictionaryLocators,
      );
      const result = await client.synthesize(request, {
        destination: 'ElevenLabs',
        grantedAt: new Date().toISOString(),
        contentSha256: sourceHash,
      });
      await onSave(await trackFromSynthesis(track, tts, sourceHash, result, effectiveVoice?.name));
      setShowConsent(false);
      setConsentChecked(false);
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(undefined);
    }
  }, [
    sourceHash,
    effectiveText,
    effectiveVoiceId,
    effectiveVoice,
    tts,
    state.course,
    pronunciationDictionaryLocators,
    client,
    track,
    onSave,
  ]);

  const replaceLexicon = useCallback(async (lexicon: readonly PronunciationEntry[]) => {
    await state.updateCourse((draft) => {
      draft.tts.lexicon = [...lexicon];
      draft.tts.lexiconVersion += 1;
      draft.tts.pronunciationDictionary = undefined;
    });
  }, [state]);

  const addLexiconEntry = useCallback(async () => {
    const parsed = pronunciationEntrySchema.safeParse({
      written: lexiconWritten,
      pronunciation: lexiconPronunciation,
      alphabet: 'alias',
      locale: state.course.language,
    });
    if (!parsed.success) return;
    const withoutDuplicate = state.course.tts.lexicon.filter(
      (entry) => entry.written.toLocaleLowerCase() !== parsed.data.written.toLocaleLowerCase(),
    );
    await replaceLexicon([...withoutDuplicate, parsed.data]);
    setLexiconWritten('');
    setLexiconPronunciation('');
  }, [lexiconPronunciation, lexiconWritten, replaceLexicon, state.course.language, state.course.tts.lexicon]);

  const exportLexicon = useCallback(() => {
    const url = URL.createObjectURL(new Blob([
      JSON.stringify(state.course.tts.lexicon, null, 2),
    ], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `slideforge-${state.course.courseId}-pronunciation.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [state.course.courseId, state.course.tts.lexicon]);

  const importLexicon = useCallback(async (file: File | undefined) => {
    if (!file) return;
    setError(undefined);
    try {
      const parsed = pronunciationEntrySchema.array().parse(JSON.parse(await file.text()));
      await replaceLexicon(parsed);
    } catch (caught) {
      setError(messageOf(caught));
    }
  }, [replaceLexicon]);

  const syncLexicon = useCallback(async () => {
    setBusy('lexicon');
    setError(undefined);
    try {
      const content = pronunciationDictionaryConsentContent(lexiconSyncRequest);
      const result = await client.syncPronunciationDictionary(lexiconSyncRequest, {
        destination: 'ElevenLabs',
        grantedAt: new Date().toISOString(),
        contentSha256: await sha256Hex(content),
      });
      await state.updateCourse((draft) => {
        draft.tts.pronunciationDictionary = { id: result.id, versionId: result.versionId };
      });
      setShowLexiconConsent(false);
      setLexiconConsentChecked(false);
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(undefined);
    }
  }, [client, lexiconSyncRequest, state]);

  const prepareBatch = useCallback(async (mode: 'all' | 'stale') => {
    const slides = state.deck.parsed?.deck.slides;
    if (!slides) return;
    setBusy('batch');
    setError(undefined);
    try {
      const jobs = await Promise.all(slides.filter((slide) => !slide.hidden).map(async (slide) => {
        const slideTrack = state.course.slides[slide.id]?.narration;
        const slideTts = slideTrack?.tts ?? DEFAULT_SLIDE_TTS;
        const text = slideTts.textSource === 'script'
          ? slideTts.script
          : slideTts.textSource === 'body'
            ? slideBodyText(slide.shapes)
            : slide.notes
              ? textBodyToPlainText(slide.notes)
              : '';
        const hash = await sha256Hex(text);
        const active = slideTrack?.takes.find((take) => take.id === slideTrack.activeTakeId);
        const voiceId = slideTts.voiceId || state.course.tts.voiceId;
        const expectedKey = voiceId
          ? await synthesisCacheKey(synthesisRequestFor(
              text,
              voiceId,
              slideTts,
              state.course,
              pronunciationDictionaryLocators,
            ))
          : undefined;
        const staleSlide = active?.source === 'tts' && (
          slideTts.sourceHash !== hash ||
          slideTts.synthesisKey !== expectedKey ||
          active.synthesisKey !== expectedKey
        );
        return {
          slideId: slide.id,
          slideName: slide.name || `Slide ${slide.index + 1}`,
          text,
          sourceHash: hash,
          voiceId,
          voiceName: voices.find((voice) => voice.id === voiceId)?.name,
          tts: slideTts,
          track: slideTrack,
          stale: staleSlide,
        } satisfies BatchJob;
      }));
      setBatchJobs(mode === 'stale' ? jobs.filter((job) => job.stale) : jobs);
      setBatchConsentChecked(false);
      setShowBatchConsent(true);
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(undefined);
    }
  }, [pronunciationDictionaryLocators, state.course, state.deck.parsed, voices]);

  const runBatch = useCallback(async () => {
    const controller = new AbortController();
    batchAbortRef.current = controller;
    setBusy('batch');
    setBatchRunning(true);
    setShowBatchConsent(false);
    setError(undefined);
    const failures: string[] = [];
    let completed = 0;
    setBatchProgress({ completed, total: batchJobs.length, failures: [] });
    try {
      for (const job of batchJobs) {
        if (controller.signal.aborted) break;
        try {
          if (!job.text) throw new Error('The selected text source is empty.');
          if (!job.voiceId) throw new Error('No course or slide voice is selected.');
          const request = synthesisRequestFor(
            job.text,
            job.voiceId,
            job.tts,
            state.course,
            pronunciationDictionaryLocators,
          );
          const result = await client.synthesize(request, {
            destination: 'ElevenLabs',
            grantedAt: new Date().toISOString(),
            contentSha256: job.sourceHash,
          }, controller.signal);
          await state.setSlideData(
            job.slideId,
            'narration',
            await trackFromSynthesis(job.track, job.tts, job.sourceHash, result, job.voiceName),
          );
        } catch (caught) {
          if (controller.signal.aborted) break;
          failures.push(`${job.slideName}: ${messageOf(caught)}`);
        }
        completed += 1;
        setBatchProgress({ completed, total: batchJobs.length, failures: [...failures] });
      }
    } finally {
      batchAbortRef.current = undefined;
      setBatchRunning(false);
      setBusy(undefined);
      setBatchProgress({ completed, total: batchJobs.length, failures: [...failures] });
    }
  }, [batchJobs, client, pronunciationDictionaryLocators, state]);

  return (
    <section className="sf-tts" aria-labelledby="sf-tts-heading">
      <h3 id="sf-tts-heading">{t.ttsHeading}</h3>
      <p className="sf-note">{t.ttsCloudNotice}</p>

      <div className="sf-secret-row">
        <label className="sf-field">
          <span>{t.ttsApiKey}</span>
          <input
            type="password"
            value={apiKey}
            autoComplete="off"
            onChange={(event) => setApiKey(event.target.value)}
          />
        </label>
        <button type="button" disabled={!apiKey || busy !== undefined} onClick={() => void storeCredential()}>
          {t.ttsSaveApiKey}
        </button>
        <button type="button" disabled={busy !== undefined} onClick={() => void loadVoices()}>
          {t.ttsLoadVoices}
        </button>
      </div>

      {error && <p role="alert" className="sf-error">{error}</p>}

      <label className="sf-field">
        <span>{t.ttsTextSource}</span>
        <select
          value={tts.textSource}
          onChange={(event) => void saveTts({ ...tts, textSource: event.target.value as SlideTtsSettings['textSource'] })}
        >
          <option value="notes">{t.ttsSourceNotes}</option>
          <option value="script">{t.ttsSourceScript}</option>
          <option value="body">{t.ttsSourceBody}</option>
        </select>
      </label>

      {tts.textSource === 'script' && (
        <label className="sf-field">
          <span>{t.ttsScript}</span>
          <textarea rows={7} value={tts.script} onChange={(event) => void saveTts({ ...tts, script: event.target.value })} />
        </label>
      )}
      <div className="sf-actions">
        <button
          type="button"
          disabled={!notesText}
          onClick={() => void saveTts({ ...tts, textSource: 'script', script: notesText })}
        >
          {t.ttsCopyNotes}
        </button>
      </div>

      <fieldset className="sf-batch">
        <legend>{t.ttsBatchAll}</legend>
        <div className="sf-actions">
          <button
            type="button"
            disabled={!state.deck.parsed || lexiconNeedsSync || busy !== undefined}
            onClick={() => void prepareBatch('all')}
          >
            {t.ttsBatchAll}
          </button>
          <button
            type="button"
            disabled={!state.deck.parsed || lexiconNeedsSync || busy !== undefined}
            onClick={() => void prepareBatch('stale')}
          >
            {t.ttsBatchStale}
          </button>
          {batchRunning && (
            <button type="button" onClick={() => batchAbortRef.current?.abort()}>{t.ttsBatchCancel}</button>
          )}
        </div>
        {batchProgress && (
          <div>
            <p
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={batchProgress.total}
              aria-valuenow={batchProgress.completed}
            >
              {t.ttsBatchProgress(batchProgress.completed, batchProgress.total)}
            </p>
            {batchProgress.failures.length > 0 && (
              <ul className="sf-error">{batchProgress.failures.map((failure) => <li key={failure}>{failure}</li>)}</ul>
            )}
          </div>
        )}
      </fieldset>

      {!state.deck.parsed && tts.textSource !== 'script' && <p className="sf-warning">{t.ttsNeedsDeck}</p>}
      {!effectiveText && <p className="sf-note">{t.ttsNoText}</p>}
      <p className="sf-help">{t.ttsCharacters(effectiveText.length, deckCharacters)}</p>

      <label className="sf-field">
        <span>{t.ttsStyleFilter}</span>
        <select value={styleFilter} onChange={(event) => setStyleFilter(event.target.value)}>
          <option value="">{t.ttsAllStyles}</option>
          {styles.map((style) => <option key={style} value={style}>{style}</option>)}
        </select>
      </label>

      <label className="sf-field">
        <span>{t.ttsCourseVoice}</span>
        <select
          value={state.course.tts.voiceId}
          onChange={(event) => void state.updateCourse((draft) => { draft.tts.voiceId = event.target.value; })}
        >
          <option value="">—</option>
          {filteredVoices.map((voice) => <option key={voice.id} value={voice.id}>{voice.name}</option>)}
        </select>
      </label>

      <label className="sf-field">
        <span>{t.ttsModel}</span>
        <select
          value={state.course.tts.modelId}
          onChange={(event) => void state.updateCourse((draft) => { draft.tts.modelId = event.target.value; })}
        >
          <option value="eleven_multilingual_v2">Eleven Multilingual v2</option>
          <option value="eleven_flash_v2_5">Eleven Flash v2.5</option>
        </select>
      </label>

      <label className="sf-field">
        <span>{t.ttsSpeed}: {state.course.tts.rate.toFixed(2)}</span>
        <input
          type="range"
          min="0.7"
          max="1.2"
          step="0.05"
          value={state.course.tts.rate}
          onChange={(event) => void state.updateCourse((draft) => { draft.tts.rate = Number(event.target.value); })}
        />
      </label>

      <label className="sf-field">
        <span>{t.ttsSlideVoice}</span>
        <select
          value={tts.voiceId ?? ''}
          onChange={(event) => void saveTts({ ...tts, voiceId: event.target.value || undefined })}
        >
          <option value="">{t.ttsInheritedVoice}</option>
          {filteredVoices.map((voice) => <option key={voice.id} value={voice.id}>{voice.name}</option>)}
        </select>
      </label>

      {effectiveVoice?.sampleUrl && <audio controls preload="none" src={effectiveVoice.sampleUrl} />}

      {problems.length > 0 && (
        <details>
          <summary>{t.ttsProblems} ({problems.length})</summary>
          <ul>{problems.map((problem) => <li key={`${problem.start}:${problem.reason}`}>{problem.token}</li>)}</ul>
        </details>
      )}

      <fieldset className="sf-lexicon">
        <legend>{t.ttsLexicon}</legend>
        {state.course.tts.lexicon.map((entry, index) => (
          <div className="sf-lexicon-entry" key={`${entry.written}:${entry.pronunciation}`}>
            <span><strong>{entry.written}</strong> → {entry.pronunciation}</span>
            <button
              type="button"
              onClick={() => void replaceLexicon(state.course.tts.lexicon.filter((_, candidate) => candidate !== index))}
            >
              {t.ttsLexiconRemove}
            </button>
          </div>
        ))}
        <div className="sf-lexicon-form">
          <label className="sf-field">
            <span>{t.ttsLexiconWritten}</span>
            <input value={lexiconWritten} onChange={(event) => setLexiconWritten(event.target.value)} />
          </label>
          <label className="sf-field">
            <span>{t.ttsLexiconPronunciation}</span>
            <input value={lexiconPronunciation} onChange={(event) => setLexiconPronunciation(event.target.value)} />
          </label>
          <button
            type="button"
            disabled={!lexiconWritten.trim() || !lexiconPronunciation.trim() || busy !== undefined}
            onClick={() => void addLexiconEntry()}
          >
            {t.ttsLexiconAdd}
          </button>
        </div>
        <div className="sf-actions">
          <button type="button" disabled={state.course.tts.lexicon.length === 0} onClick={exportLexicon}>
            {t.ttsLexiconExport}
          </button>
          <label className="sf-file-button">
            <span>{t.ttsLexiconImport}</span>
            <input
              className="sf-visually-hidden"
              type="file"
              accept="application/json,.json"
              onChange={(event) => void importLexicon(event.target.files?.[0])}
            />
          </label>
          <button
            type="button"
            disabled={state.course.tts.lexicon.length === 0 || busy !== undefined}
            onClick={() => { setLexiconConsentChecked(false); setShowLexiconConsent(true); }}
          >
            {t.ttsLexiconSync}
          </button>
        </div>
        {lexiconNeedsSync
          ? <p role="status" className="sf-warning">{t.ttsLexiconNeedsSync}</p>
          : state.course.tts.pronunciationDictionary
            ? <p role="status" className="sf-note">{t.ttsLexiconSynced}</p>
            : null}
      </fieldset>

      <label className="sf-check">
        <input
          type="checkbox"
          checked={state.course.tts.syntheticDisclosure}
          onChange={(event) => void state.updateCourse((draft) => { draft.tts.syntheticDisclosure = event.target.checked; })}
        />
        <span>{t.ttsSyntheticDisclosure}</span>
      </label>

      {stale && <p role="alert" className="sf-warning">{t.ttsStale}</p>}

      {track?.tts?.wordTimings.length ? (
        <details>
          <summary>{t.ttsTimingPreview} ({track.tts.wordTimings.length})</summary>
          <div className="sf-word-timings">
            {track.tts.wordTimings.map((word) => (
              <span key={`${word.charStart}:${word.word}`} title={`${word.startMs}–${word.endMs} ms`}>{word.word}</span>
            ))}
          </div>
        </details>
      ) : null}

      <div className="sf-actions">
        <button
          type="button"
          className="sf-primary"
          disabled={!effectiveText || !effectiveVoiceId || lexiconNeedsSync || busy !== undefined}
          onClick={() => { setConsentChecked(false); setShowConsent(true); }}
        >
          {busy === 'synthesis' ? t.ttsWorking : t.ttsReviewConsent}
        </button>
      </div>

      {showConsent && (
        <div className="sf-consent" role="alertdialog" aria-modal="true" aria-labelledby="sf-consent-heading">
          <h4 id="sf-consent-heading">{t.ttsConsentHeading}</h4>
          <p>{t.ttsConsentBody(effectiveText.length)}</p>
          <label className="sf-check">
            <input type="checkbox" checked={consentChecked} onChange={(event) => setConsentChecked(event.target.checked)} />
            <span>{t.ttsConsentConfirm}</span>
          </label>
          <div className="sf-actions">
            <button type="button" className="sf-primary" disabled={!consentChecked || busy !== undefined} onClick={() => void synthesize()}>
              {t.ttsSynthesize}
            </button>
            <button type="button" disabled={busy !== undefined} onClick={() => setShowConsent(false)}>{t.ttsCancel}</button>
          </div>
        </div>
      )}

      {showLexiconConsent && (
        <div className="sf-consent" role="alertdialog" aria-modal="true" aria-labelledby="sf-lexicon-consent-heading">
          <h4 id="sf-lexicon-consent-heading">{t.ttsLexiconConsentHeading}</h4>
          <p>{t.ttsLexiconConsentBody(state.course.tts.lexicon.length)}</p>
          <label className="sf-check">
            <input
              type="checkbox"
              checked={lexiconConsentChecked}
              onChange={(event) => setLexiconConsentChecked(event.target.checked)}
            />
            <span>{t.ttsLexiconConsentConfirm}</span>
          </label>
          <div className="sf-actions">
            <button
              type="button"
              className="sf-primary"
              disabled={!lexiconConsentChecked || busy !== undefined}
              onClick={() => void syncLexicon()}
            >
              {t.ttsLexiconSync}
            </button>
            <button type="button" disabled={busy !== undefined} onClick={() => setShowLexiconConsent(false)}>{t.ttsCancel}</button>
          </div>
        </div>
      )}

      {showBatchConsent && (
        <div className="sf-consent" role="alertdialog" aria-modal="true" aria-labelledby="sf-batch-consent-heading">
          <h4 id="sf-batch-consent-heading">{t.ttsBatchConsentHeading}</h4>
          {batchJobs.length === 0 ? (
            <p>{t.ttsBatchNoSlides}</p>
          ) : (
            <>
              <p>{t.ttsBatchConsentBody(
                batchJobs.length,
                batchJobs.reduce((total, job) => total + job.text.length, 0),
              )}</p>
              <label className="sf-check">
                <input
                  type="checkbox"
                  checked={batchConsentChecked}
                  onChange={(event) => setBatchConsentChecked(event.target.checked)}
                />
                <span>{t.ttsConsentConfirm}</span>
              </label>
            </>
          )}
          <div className="sf-actions">
            {batchJobs.length > 0 && (
              <button
                type="button"
                className="sf-primary"
                disabled={!batchConsentChecked || busy !== undefined}
                onClick={() => void runBatch()}
              >
                {t.ttsSynthesize}
              </button>
            )}
            <button type="button" disabled={busy !== undefined} onClick={() => setShowBatchConsent(false)}>{t.ttsCancel}</button>
          </div>
        </div>
      )}
    </section>
  );
}

function slideBodyText(shapes: Parameters<typeof walkShapes>[0]): string {
  const blocks: string[] = [];
  for (const shape of walkShapes(shapes)) {
    if ((shape.kind === 'shape' || shape.kind === 'graphicFrame') && shape.text) {
      blocks.push(textBodyToPlainText(shape.text));
    } else if (shape.kind === 'table') {
      for (const row of shape.rows) for (const cell of row.cells) blocks.push(textBodyToPlainText(cell.text));
    }
  }
  return blocks.filter(Boolean).join('\n');
}

interface BatchJob {
  readonly slideId: string;
  readonly slideName: string;
  readonly text: string;
  readonly sourceHash: string;
  readonly voiceId: string;
  readonly voiceName?: string;
  readonly tts: SlideTtsSettings;
  readonly track?: NarrationTrack;
  readonly stale: boolean;
}

interface BatchProgress {
  readonly completed: number;
  readonly total: number;
  readonly failures: readonly string[];
}

function synthesisRequestFor(
  text: string,
  voiceId: string,
  tts: SlideTtsSettings,
  course: CourseData,
  pronunciationDictionaryLocators: SynthesisRequest['pronunciationDictionaryLocators'],
): SynthesisRequest {
  return {
    text,
    voiceId,
    modelId: tts.modelId ?? course.tts.modelId,
    locale: course.language,
    rate: course.tts.rate,
    pitch: course.tts.pitch,
    volume: course.tts.volume,
    pauseAfterSentenceMs: course.tts.pauseAfterSentenceMs,
    lexiconVersion: course.tts.lexiconVersion,
    ...(pronunciationDictionaryLocators ? { pronunciationDictionaryLocators } : {}),
  };
}

async function trackFromSynthesis(
  track: NarrationTrack | undefined,
  tts: SlideTtsSettings,
  sourceHash: string,
  result: SynthesisResult,
  voiceName: string | undefined,
): Promise<NarrationTrack> {
  const audioBytes = new Uint8Array(result.audio.byteLength);
  audioBytes.set(result.audio);
  const clip = await putClip(new Blob([audioBytes.buffer], { type: result.mime }));
  const take: NarrationTake = {
    id: `take-tts-${Date.now().toString(36)}-${result.synthesisKey.slice(0, 8)}`,
    source: 'tts',
    mimeType: result.mime,
    durationMs: result.durationMs,
    byteLength: clip.byteLength,
    audioId: clip.audioId,
    recordedAt: new Date().toISOString(),
    label: voiceName ?? 'ElevenLabs',
    synthesisKey: result.synthesisKey,
  };
  const base = track ?? emptyNarrationTrack();
  const generatedTts = slideTtsSettingsSchema.parse({
    ...tts,
    sourceHash,
    synthesisKey: result.synthesisKey,
    generatedAt: new Date().toISOString(),
    wordTimings: result.wordTimings,
    sentenceTimings: result.sentenceTimings,
  });
  return {
    ...base,
    takes: [...base.takes, take],
    activeTakeId: take.id,
    captions: [...result.captions],
    syncMarkers: [...syncMarkersFromCharacterOffsets(generatedTts.animationTriggerOffsets, result.wordTimings)],
    tts: generatedTts,
  };
}

function messageOf(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}
